import { fadeOutAfter, play, stop } from '../core/audio'
import { clear, h, isAbort, wait } from '../core/dom'
import { haptic } from '../core/haptics'
import { back } from '../core/router'
import { getSettings, markRitualDone } from '../core/store'
import { shutUp, speak } from '../core/voice'
import { DRIFT_SCRIPTS, type DriftScript } from '../data/driftScripts'
import { getLang, num, t } from '../i18n'
import { topbar } from './shared'

export function renderDrift(root: HTMLElement, signal: AbortSignal): void {
  signal.addEventListener(
    'abort',
    () => {
      shutUp()
      stop()
    },
    { once: true },
  )
  pick(root, signal)
}

function pick(root: HTMLElement, signal: AbortSignal): void {
  clear(root)
  const d = t()
  const lang = getLang()

  root.append(
    topbar(d.drift.title),
    h(
      'div',
      { class: 'stack fade-in' },
      h('p', { class: 'section-label' }, d.drift.pick),
      ...DRIFT_SCRIPTS.map((s) =>
        h(
          'button',
          {
            class: 'tile',
            onclick: () => {
              haptic.tap()
              void run(root, signal, s)
            },
          },
          h(
            'span',
            { class: 'grow' },
            h('span', { class: 'label', style: { display: 'block' } }, s.title[lang]),
            h('span', { class: 'sub', style: { display: 'block' } }, s.blurb[lang]),
          ),
          h('span', { class: 'sub' }, `${num(s.minutes)} ${d.common.minutes}`),
        ),
      ),
    ),
  )
}

async function run(root: HTMLElement, signal: AbortSignal, script: DriftScript): Promise<void> {
  clear(root)
  const d = t()
  const lang = getLang()

  // Each script names its own sound bed; rain under a rain story, drone under
  // the tablet room. The bed carries the silences between lines.
  await play(script.bed, getSettings().soundVolume * 0.55)

  const line = h('div', { class: 'drift-line' })
  const bar = h('i', { style: { width: '0%' } })

  root.append(
    topbar(script.title[lang]),
    h('div', { class: 'drift-stage fade-in' }, line),
    h(
      'div',
      { class: 'stack-sm' },
      h('div', { class: 'drift-progress' }, bar),
      h(
        'button',
        {
          class: 'btn ghost block',
          onclick: () => {
            haptic.tap()
            back()
          },
        },
        d.common.stop,
      ),
    ),
  )

  try {
    for (let i = 0; i < script.lines.length; i++) {
      const entry = script.lines[i]!
      const text = entry.text[lang]

      line.style.opacity = '0'
      await wait(600, signal)
      line.textContent = text
      line.style.transition = 'opacity 1.4s var(--ease)'
      line.style.opacity = '1'
      bar.style.width = `${Math.round(((i + 1) / script.lines.length) * 100)}%`

      await speak(text, { rate: 0.68, pitch: 0.88 })
      await wait(entry.pause * 1000, signal)
    }

    markRitualDone('drift')
    line.style.opacity = '0'
    // The script ends but the sound does not stop dead — that would be a cue to
    // wake up. It fades over the next twenty minutes instead.
    fadeOutAfter(20)
    haptic.soft()
  } catch (err) {
    if (!isAbort(err)) throw err
  }
}
